/**
 * R5's trailing overflow. `@sportsmet/ui` ships no dropdown or menu, so this
 * is a `Dialog`, a bottom sheet under 768px and a centered dialog at 768px
 * and up from one component. Its open state is driven by `?sheet=`, owned
 * by the route, never by component state.
 *
 * Two items: `Report` (ISSUE-1375, the same account report the pool member
 * menu and mobile already file) and `Block`. Report hands off to the route,
 * which swaps this sheet for `FanReportDialog` through `?sheet=`, so the two
 * overlays never stack.
 *
 * The name on the block confirm is read from the profile query here, keyed on
 * the `accountId` the URL already carries, rather than handed down from the
 * button that opened the sheet. The open state lives in the URL, so a reload
 * or a returned-to tab reopens the sheet with no such handoff, and the
 * confirm used to ask "Block ?" with nobody named. `accountId` is the only
 * thing the URL is the source of truth for; the name is derived from it.
 *
 * While that read is still in flight the account has no name yet, so Block is
 * not offered. Blocking is hard to walk back, and a confirm that cannot say
 * who it is about must never be reachable.
 */
import { useProfileGetForAccount } from "@sportsmet/profile-sdk/private";
import { Button, Dialog, InlineBanner } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { useBlockFan } from "../messageModeration/useBlockFan";
import {
  BLOCK_ACTION_LABEL,
  FAN_ACTIONS_TITLE,
  REPORT_FAN_ACTION_LABEL,
  PROFILE_READ_FAILED_LINE,
  PROFILE_READ_PENDING_LABEL,
  PROFILE_READ_RETRY_BUSY_LABEL,
  PROFILE_READ_RETRY_LABEL,
} from "./fanProfileCopy";

export type typeFanActionsDialogProps = {
  /** Driven by ?sheet=fan-actions, never by component state. */
  open: boolean;
  /** Clears ?sheet=. */
  onClose: () => void;
  accountId: string;
  /** Swaps this sheet for the report sheet. */
  onReport: () => void;
};

export const FanActionsDialog = (
  props: typeFanActionsDialogProps,
): ReactNode => {
  const { blockFan, isPending } = useBlockFan();

  // The same query key `FanProfilePane` reads behind this sheet, so on the
  // ordinary path react-query answers from cache and no second request goes
  // out.
  const profileQuery = useProfileGetForAccount(
    { accountId: props.accountId },
    { enabled: props.open && props.accountId !== "" },
  );
  const displayName = profileQuery.data?.data?.profile?.displayName ?? "";
  const isNameReady = displayName !== "";
  const hasReadFailed =
    profileQuery.isError || Boolean(profileQuery.data?.error);
  const isReadInFlight = profileQuery.isFetching;

  const handleBlock = async (): Promise<void> => {
    // Belt and braces with the disabled Button below: neither the keyboard
    // nor a stray programmatic click can raise a confirm with no name in it.
    if (!isNameReady) return;
    const outcome = await blockFan({
      accountId: props.accountId,
      displayName,
    });
    if (outcome.didBlock) props.onClose();
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title={FAN_ACTIONS_TITLE}
    >
      {hasReadFailed ? (
        <InlineBanner
          variant="warning"
          message={PROFILE_READ_FAILED_LINE}
          action={{
            label: PROFILE_READ_RETRY_LABEL,
            onAction: () => {
              void profileQuery.refetch();
            },
            loading: isReadInFlight,
            loadingLabel: PROFILE_READ_RETRY_BUSY_LABEL,
          }}
        />
      ) : null}
      <Button
        variant="ghost"
        fullWidth
        disabled={!isNameReady}
        data-testid="fan-report-action"
        onClick={() => {
          props.onReport();
        }}
      >
        {REPORT_FAN_ACTION_LABEL}
      </Button>
      <Button
        variant="ghost"
        fullWidth
        disabled={isPending || !isNameReady}
        loading={isReadInFlight && !isNameReady}
        loadingLabel={PROFILE_READ_PENDING_LABEL}
        data-testid="fan-block-action"
        onClick={() => {
          void handleBlock();
        }}
      >
        {BLOCK_ACTION_LABEL}
      </Button>
    </Dialog>
  );
};
